import SharedDialog from '../../../../shared/shared-dialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { copyDocument, UserKeys } from '../../../../api/users-api';
import { handleErrors } from '../../../../utils/errors';
import { Doc } from '../../../../../types';
import { useState } from 'react';
import ChangeLogDialog from '../../../../shared/change-log-dialog';
import { useStore } from '../../../state/zustand-store';
import { useAnalytics } from '../../../../shared/analytics/create-events';
import * as analytics from '../../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../../shared/analytics/module-list';
import {
  DriveRoles,
  isAbleToSeeUIElement,
} from '../../../../utils/roles/client-roles';
import { Messages } from '../../../../utils/message-constants';

import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

interface DriveCopyDialogProps {
  open: boolean;
  handleClose: () => void;
  userEmail: string;
  clickedDoc: Doc;
  selectedFolder: string;
  driveId: string;
  changeLogRoles: string[];
  isAllUsers?: boolean;
}

export default function DriveCopyDialog({
  open,
  handleClose,
  userEmail,
  clickedDoc,
  selectedFolder,
  driveId,
  changeLogRoles,
  isAllUsers,
}: DriveCopyDialogProps) {
  const { createEvent } = useAnalytics();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const { setSnackbar } = useStore();
  const [newName, setNewName] = useState('Copy of ' + clickedDoc.title);
  const queryClient = useQueryClient();

  const { mutate, isPending: isCopying } = useMutation({
    mutationFn: () =>
      copyDocument(
        userEmail,
        clickedDoc.id,
        newName.trim(),
        selectedFolder,
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Copied('Document'),
      });

      queryClient.invalidateQueries({
        queryKey: [UserKeys.DriveDocuments, userEmail, selectedFolder],
      });

      if (selectedFolder !== `${driveId}.starred` && clickedDoc.starred) {
        queryClient.invalidateQueries({
          queryKey: [UserKeys.DriveDocuments, userEmail, `${driveId}.starred`],
        });
      }

      setChangeLogMessage('');
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Copying(
          'the selected document'
        ),
      });
    },
    onSettled: () => {
      if (isAllUsers) {
        createEvent(analytics.drive.drive_explorer.copy_drive_document, {
          module: ModuleList.modules.drive.name,
          section: ModuleList.modules.drive.sections.driveExplorer,
        });
      } else {
        createEvent(
          analytics.user_management.user_details.drive.copy_drive_document,
          {
            module: ModuleList.modules.userManagement.name,
            section: ModuleList.modules.userManagement.sections.drive,
          }
        );
      }
      setOpenChangeLogDialog(false);
    },
  });

  function handleSubmit() {
    if (isAbleToSeeUIElement(changeLogRoles, DriveRoles.CanCopyDocs.value)) {
      setOpenChangeLogDialog(true);
    } else {
      mutate();
    }
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      title="Copy Document"
      actingAs={userEmail}
      dialogContent={
        <Stack gap={1}>
          <Typography>Provide a name for the copied document.</Typography>
          <TextField
            fullWidth
            autoFocus
            value={newName}
            label={'Name'}
            onChange={(e) => setNewName(e.target.value)}
          />
          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={mutate}
            mutateLoading={isCopying}
          />
        </Stack>
      }
      primaryButton={{
        title: 'Copy Document',
        action: handleSubmit,
        loading: isCopying,
        disabled: isCopying || newName.trim().length === 0,
      }}
    />
  );
}
